import React from 'react'
import PropTypes from 'prop-types'
import { reduxForm, Field, SubmissionError, formValues } from 'redux-form'
import { Button, Heading, Hyperlink, Notification, SubmitButton } from '@offerzen/pinata-shame'
import { FileUpload } from '@offerzen/pinata-shame/uploaders'
import { resumeParsingStatuses } from '../../../CandidateExperience/hooks'

import styles from './styles'

const { FAILED, ERRORED, QUEUED } = resumeParsingStatuses

const ResumeForm = props => (
  <form onSubmit={props.handleSubmit}>
    <Heading className={styles.heading}>Import your CV to get a head start on your profile</Heading>

    <p className={styles.text}>
      Upload your CV, resume, or&nbsp;
      <Hyperlink
        label="LinkedIn profile PDF"
        href="https://www.linkedin.com/help/linkedin/answer/4281/save-a-profile-as-a-pdf"
        external
        target="_blank"
        rel="noopener noreferrer"
      />
      &nbsp;and we&apos;ll use it to complete as much of your profile as we can.
    </p>

    <Field
      component={FileUpload}
      size="small"
      id="resume"
      name="resume"
      sizeLimit={6}
      helpText="Supported file formats: PDF, .docx, .xls and more"
      className={styles.upload}
      onChange={() => {
        props.trackSelectedFile()
        props.resetResumeParser()
      }}
    />

    <If condition={props.resumeParsingExecutionStatus === ERRORED || props.error || props.hasPolledTooManyTimes}>
      <Notification theme="error" className={styles.error}>
        Something went wrong and we couldn&apos;t import your information. Please upload your CV again.
      </Notification>
    </If>

    <If condition={props.resumeParsingExecutionStatus === FAILED}>
      <Notification theme="warning" className={styles.error}>
        We couldn&apos;t import any information from that file. Please try a different file.
      </Notification>
    </If>

    <div className={styles.cardFooter}>
      <Choose>
        <When condition={props.resume && props.resume[0]}>
          <Button label="Next" type="submit" size="medium" />
        </When>
        <When condition={props.loadingCandidate}>
          <SubmitButton variant="outlined" isSubmitting={true} submittingLabel="Loading" />
        </When>
        <Otherwise>
          <Button label="Skip" onClick={props.updateCandidate} size="medium" variant="outlined" />
        </Otherwise>
      </Choose>
    </div>
  </form>
)

ResumeForm.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  importResume: PropTypes.func,
  resumeParsingExecutionStatus: PropTypes.oneOf([QUEUED, FAILED, ERRORED]),
  hasPolledTooManyTimes: PropTypes.bool,
  handleSubmit: PropTypes.func,
  trackSelectedFile: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.any, // Redux Form docs say error is of type `any`s
  updateCandidate: PropTypes.func,
  loadingCandidate: PropTypes.bool,
  resume: PropTypes.shape({
    0: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  resetResumeParser: PropTypes.func.isRequired,
}

export default reduxForm({
  form: 'resume',
  onSubmit: (values, _, props) =>
    new Promise((resolve, reject) => {
      props
        .importResume({ file: values.resume[0] })
        .then(() => resolve())
        .catch(() =>
          reject(
            new SubmissionError({
              _error: 'Something went wrong. Please try again',
            }),
          ),
        )
    }),
})(formValues({ resume: 'resume' })(ResumeForm))
